import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

// deck.gl loaded from CDN on demand (node_modules is root-owned; can't npm-install here)
function loadDeck(): Promise<any> {
  const w = window as any;
  if (w.deck) return Promise.resolve(w.deck);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/deck.gl@9.0.38/dist.min.js';
    s.onload = () => resolve(w.deck); s.onerror = () => reject(new Error('deck.gl CDN load failed'));
    document.head.appendChild(s);
  });
}
function ramp(z: number, zmax: number) {
  if (z < 1) return [110, 80, 55];
  if (z < 5) return [150, 140, 95];
  const t = Math.min(1, (z - 5) / Math.max(8, zmax - 5));
  return [Math.round(120 - 90 * t), Math.round(205 - 30 * t), Math.round(120 - 60 * t)];
}

export default function PointCloudViewer({ segmentId, onClose }: { segmentId: string; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const deckRef = useRef<any>(null);
  const [msg, setMsg] = useState('Loading LiDAR…');

  useEffect(() => {
    let cancelled = false;
    const poll = async (tries: number): Promise<any> => {
      try { const d = await api.get(`/bead/segments/${segmentId}/pointcloud`); if (d && d.points && d.points.length) return d; } catch { /* 404 until ready */ }
      if (tries === 0) { try { await api.post(`/bead/segments/${segmentId}/pointcloud/run`, {}); } catch { /* */ } }
      if (tries > 30) throw new Error('timed out fetching LiDAR tile');
      setMsg('Fetching + clipping USGS 3DEP tile… (~30–60 s, one-time per span)');
      await new Promise((r) => setTimeout(r, 4000));
      return poll(tries + 1);
    };
    (async () => {
      try {
        const [deckNS, data] = await Promise.all([loadDeck(), poll(0)]);
        if (cancelled || !canvasRef.current) return;
        const { Deck, OrbitView, PointCloudLayer, PathLayer, COORDINATE_SYSTEM } = deckNS;
        const pts: number[][] = data.points || [];
        const zmax = pts.reduce((m, p) => Math.max(m, p[2]), 1);
        const layers = [
          new PointCloudLayer({
            id: 'pc', data: pts, coordinateSystem: COORDINATE_SYSTEM.CARTESIAN,
            getPosition: (d: any) => d, getColor: (d: any) => ramp(d[2], zmax), pointSize: 1.7,
          }),
          new PathLayer({
            id: 'wire', data: [{ path: (data.line || []).map((p: any) => [p[0], p[1], 9]) }],
            coordinateSystem: COORDINATE_SYSTEM.CARTESIAN, getPath: (d: any) => d.path,
            getColor: [37, 99, 235], getWidth: 0.8, widthUnits: 'meters', widthMinPixels: 2,
          }),
        ];
        deckRef.current = new Deck({
          canvas: canvasRef.current,
          views: [new OrbitView({ orbitAxis: 'Z' })], controller: true,
          initialViewState: { target: [0, 0, zmax / 3], rotationX: 24, rotationOrbit: 25, zoom: 3, minZoom: -2, maxZoom: 12 },
          layers,
        });
        setMsg(`${pts.length.toLocaleString()} LiDAR points`);
      } catch (e: any) {
        if (!cancelled) setMsg('Could not load point cloud: ' + (e?.message || String(e)));
      }
    })();
    return () => { cancelled = true; try { deckRef.current?.finalize?.(); } catch { /* */ } };
  }, [segmentId]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', inset: '3%', borderRadius: 10, overflow: 'hidden', background: '#000' }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
        <div style={{ position: 'absolute', top: 12, left: 14, color: '#e2e8f0', fontSize: 13, textShadow: '0 1px 3px #000' }}>
          <b>🛰 LiDAR point cloud</b> — USGS 3DEP, colored by height · drag to orbit, scroll to zoom
          <div style={{ color: '#fbbf24', marginTop: 4 }}>{msg}</div>
        </div>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 14, background: '#4338ca', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );
}
